import React, { useEffect, useState } from "react";
import axios from "axios";
import { Authorization } from "../../Context/Authorization";
import Preloader from "../../shared/PreLoaders";
import "../../dashboard/reporttable.css";
import { toast } from "react-toastify"
const inputStyle = { width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff", };

const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "flex", alignItems: "center", gap: 5, };
const TogglePill = ({
    label,
    icon,
    checked,
    onChange,
    disabled = false,
}) => (
    <label
        style={{
            display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 999, border: "1px solid #2E7D32", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, background: checked ? "#E8F5E9" : "#fff",
        }}
    >
        <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            style={{ display: "none" }}
        />
        <i className={`mdi ${icon}`} />
        {label}
    </label>
);
const MonthlyLoanRecoveryReview = () => {
    const Api = process.env.REACT_APP_API;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    /* Master Data */
    const [circles, setCircles] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [jfmcTypes, setJfmcTypes] = useState([]);
    const [loanTypes, setLoanTypes] = useState([]);

    /* Report */
    const [reviewData, setReviewData] = useState([]);
    /* Filters */
    const today = new Date();
    const [selectedMonth, setSelectedMonth] = useState(
        `${today.getFullYear()}-${String(today.getMonth()).padStart(2, "0")}`
    );
    const [year, month] = selectedMonth.split("-");
    const [selectedCircle, setSelectedCircle] = useState("");
    const [selectedDivision, setSelectedDivision] = useState("");
    const [selectedRange, setSelectedRange] = useState("");
    const [selectedJfmcType, setSelectedJfmcType] = useState("");
    const [selectedType, setSelectedType] = useState("");
    const [showCircle, setShowCircle] = useState(false);
    const [showDivision, setShowDivision] = useState(false);
    const [expandedDivisions, setExpandedDivisions] = useState({});
    const [expandedRanges, setExpandedRanges] = useState({});
    /* -------------------------------------- */
    const fetchMasterData = async () => {
        try {
            const config = Authorization();

            const [circleRes, divisionRes, jfmcRes, loanTypeRes] =
                await Promise.all([
                    axios.get(`${Api}/api/circles`, config),
                    axios.get(`${Api}/api/divisions`, config),
                    axios.get(`${Api}/api/jfmc-types`, config),
                    axios.get(`${Api}/api/loan-types`, config),
                ]);

            setCircles(circleRes.data || []);
            setDivisions(divisionRes.data || []);
            setLoanTypes(loanTypeRes.data.loanTypes || []);

            const jfmcTypeList = jfmcRes.data || [];
            setJfmcTypes(jfmcTypeList);

            const defaultJfmcType = jfmcTypeList.find(
                x => x.jfmcTypeName === "VFC"
            );

            if (defaultJfmcType) {
                setSelectedJfmcType(defaultJfmcType._id);

                // Immediately fetch using the known id
                fetchEcoTourismReview(defaultJfmcType._id);
            }
            const defaultLoanType = loanTypeRes.data.loanTypes.find(
                item => item.loanName === "Department Loan"
            );
            if (defaultLoanType) {
                setSelectedType(defaultLoanType._id);
            }
        } catch (err) {
            console.error(err);
        }
    };
    /* -------------------------------------- */
    useEffect(() => {
        fetchMasterData();
    }, []);
    /* -------------------------------------- */
    useEffect(() => {
        if (!selectedJfmcType) return;

        fetchEcoTourismReview(selectedJfmcType);
    }, [
        selectedMonth,
        selectedCircle,
        selectedDivision,
        selectedJfmcType,
        selectedType
    ]);
    /* -------------------------------------- */
    const fetchEcoTourismReview = async (
        jfmcTypeId = selectedJfmcType
    ) => {
        try {
            setLoading(true);

            const config = Authorization();

            const res = await axios.post(
                `${Api}/api/loan-collections/recovery/review`,
                {
                    month,
                    year,
                    circleId: selectedCircle || undefined,
                    divisionId: selectedDivision || undefined,
                    rangeId: selectedRange || undefined,
                    jfmcTypeId: jfmcTypeId || undefined,
                    loanTypeId: selectedType || undefined,
                },
                config
            );

            setReviewData(res.data.data || []);
        } catch (error) {
            console.error("Bank balance Review Error:", error);

            setReviewData([])
            // Optional: show a toast or alert
            toast.error(
                error.response?.data?.message || "Failed to load Loan recovery review data."
            );

        } finally {
            setLoading(false);
        }
    };


    const activeJfmc =
        jfmcTypes.find(item => item._id === selectedJfmcType) || {
            jfmcTypeName: "All JFMC Types",
        };
    const activeLoanType =
        loanTypes.find((loan) => loan._id === selectedType) || null;
    /* -------------------------------------- */
    const badge = (value, type) => {
        const styles = {
            total: {
                bg: "#EEF2FF",
                color: "#3730A3",
            },
            held: {
                bg: "#DCFCE7",
                color: "#166534",
            },
            pending: {
                bg: "#FEE2E2",
                color: "#991B1B",
            },
        };
        const style = styles[type];
        return (
            <span
                style={{ display: "inline-block", minWidth: 40, textAlign: "center", padding: "4px 10px", borderRadius: 20, background: style.bg, color: style.color, fontWeight: 600, fontSize: 12, }}>
                {value}
            </span>
        );
    };
    /* ---------------- Summary ---------------- */
    const totals = reviewData.reduce(
        (acc, item) => {
            acc.totalVfcs += item.totalVfcs || 0;
            acc.totalCollections += item.totalCollections || 0;
            acc.totalLoanAmount += item.totalLoanAmount || 0;
            acc.totalRecovery += item.totalRecovery || 0;

            acc.totalCircles = reviewData.length;

            if (item.divisions) {
                acc.totalDivisions += Object.keys(item.divisions).length;
            }

            if (item.ranges) {
                acc.totalRanges += Object.keys(item.ranges).length;
            }

            return acc;
        },
        {
            totalVfcs: 0,
            totalCollections: 0,
            totalLoanAmount: 0,
            totalRecovery: 0,
            totalCircles: 0,
            totalDivisions: 0,
            totalRanges: 0,
        }
    );
    const toggleDivision = (id) => {
        setExpandedDivisions(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };
    const toggleRange = (id) => {
        setExpandedRanges(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };
    /* -------------------------------------- */
    if (loading) return <Preloader loading />;
    if (error) return <p className="text-danger">{error}</p>;
    return <div><div className="rd-page-header">
        <div className="rd-header-left">
            <span className="page-title-icon">
                <i className="mdi mdi-clipboard-check-multiple-outline" />
            </span>
            <div>
                <h3 className="rd-page-title">
                    Loan Recovery Review Dashboard
                </h3>
                <p className="rd-page-sub">
                    Month-wise Loan Recovery Review
                </p>
            </div>
        </div>
    </div>
        {/* ---------------- Filters ---------------- */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                    <i className="mdi mdi-filter-variant" />
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Filter Report</span>
                <span style={{ fontSize: 11, color: "#A0AEC0", marginLeft: 4 }}>Toggle filters and select a date to view records</span>
            </div>
            {/* Toggle pills */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                <TogglePill label="Circle" icon="mdi-circle-outline" checked={showCircle} disabled={showDivision} onChange={(e) => { setShowCircle(e.target.checked); if (!e.target.checked) setSelectedCircle(""); }} />
                <TogglePill label="Division" icon="mdi-domain" checked={showDivision} disabled={showCircle} onChange={(e) => { setShowDivision(e.target.checked); if (!e.target.checked) setSelectedDivision(""); }} />
            </div>
            {/* Conditional selects */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12, marginBottom: 16 }}>
                <div >
                    <label style={labelStyle}>
                        <i
                            className="mdi mdi-calendar-month"
                            style={{ color: "#43A047" }}
                        />
                        Month
                    </label>
                    <input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        style={inputStyle}
                    />
                </div>
                <div>
                    <label style={labelStyle}>
                        <i
                            className="mdi mdi-shape-outline"
                            style={{ color: "#43A047" }}
                        />
                        JFMC Type
                    </label>
                    <select
                        value={selectedJfmcType}
                        onChange={(e) => setSelectedJfmcType(e.target.value)}
                        style={inputStyle}
                    >
                        <option value="">All JFMC Types</option>

                        {jfmcTypes
                            .sort((a, b) => a.jfmcTypeName.localeCompare(b.jfmcTypeName))
                            .map((j) => (
                                <option key={j._id} value={j._id}>
                                    {j.jfmcTypeName}
                                </option>
                            ))}
                    </select>
                </div>
                <div>
                    <label style={labelStyle}>
                        <i
                            className="mdi mdi-shape-outline"
                            style={{ color: "#43A047" }}
                        />
                        Select Type
                    </label>
                    <select
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                        style={inputStyle}
                    >
                        <option value="">All Loan Type</option>
                        {loanTypes.map((loan) => <option key={loan._id} value={loan._id}>{loan.loanName}</option>)}
                    </select>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12, marginBottom: 16 }}>{showCircle && (
                <div>
                    <label style={labelStyle}><i className="mdi mdi-circle-outline" style={{ color: "#43A047" }} /> Circle</label>
                    <select value={selectedCircle} onChange={(e) => setSelectedCircle(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}>
                        <option value="">— Select —</option>
                        {circles.sort((a, b) => a.circleName.localeCompare(b.circleName)).map((c) => <option key={c._id} value={c._id}>{c.circleName}</option>)}
                    </select>
                </div>
            )}
                {showDivision && (
                    <div>
                        <label style={labelStyle}><i className="mdi mdi-domain" style={{ color: "#43A047" }} /> Division</label>
                        <select value={selectedDivision} onChange={(e) => setSelectedDivision(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}>
                            <option value="">— Select —</option>
                            {divisions.sort((a, b) => a.divisionName.localeCompare(b.divisionName)).map((d) => <option key={d._id} value={d._id}>{d.divisionName}</option>)}
                        </select>
                    </div>
                )}
            </div>
        </div>
        <div className="dashboard-sections">
            <div className="jfmc-section">
                <h3 className="jfmc-title">
                    {activeJfmc.jfmcTypeName}
                    {activeLoanType ? ` - ${activeLoanType.loanName}` : ""}
                    {" "}— Loan Review Overview
                </h3>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
                        gap: 16,
                    }}
                >
                    {/* Level Count */}
                    <div className="stat-card card-blue stat-centered">
                        <div className="stat-icon-big icon-blue">
                            <i
                                className={
                                    selectedRange
                                        ? "fas fa-building"
                                        : selectedDivision
                                            ? "fas fa-map"
                                            : selectedCircle
                                                ? "fas fa-layer-group"
                                                : "fas fa-sitemap"
                                }
                            />
                        </div>

                        <div className="stat-title">
                            {selectedRange
                                ? "VFCs"
                                : selectedDivision
                                    ? "Ranges"
                                    : selectedCircle
                                        ? "Divisions"
                                        : "Circles"}
                        </div>

                        <div
                            style={{
                                fontSize: 34,
                                fontWeight: 700,
                                marginTop: 12,
                            }}
                        >
                            {selectedRange
                                ? totals.totalVfcs
                                : selectedDivision
                                    ? totals.totalRanges
                                    : selectedCircle
                                        ? totals.totalDivisions
                                        : totals.totalCircles}
                        </div>
                    </div>

                    {/* Total VFC */}
                    <div className="stat-card card-purple stat-centered">
                        <div className="stat-icon-big icon-purple">
                            <i className="fas fa-users" />
                        </div>

                        <div className="stat-title">
                            Total VFCs
                        </div>

                        <div
                            style={{
                                fontSize: 34,
                                fontWeight: 700,
                                marginTop: 12,
                            }}
                        >
                            {totals.totalVfcs}
                        </div>
                    </div>
                    {/* Loan Amount */}
                    <div className="stat-card card-orange stat-centered">
                        <div className="stat-icon-big icon-orange">
                            <i className="fas fa-rupee-sign" />
                        </div>

                        <div className="stat-title">
                            Loan Amount
                        </div>

                        <div
                            style={{
                                fontSize: 30,
                                fontWeight: 700,
                                marginTop: 12,
                            }}
                        >
                            ₹{Number(totals.totalLoanAmount || 0).toLocaleString("en-IN")}
                        </div>
                    </div>
                    {/* Total Loans totalRecovery*/}
                    <div className="stat-card card-green stat-centered">
                        <div className="stat-icon-big icon-green">
                            <i className="fas fa-file-invoice-dollar" />
                        </div>

                        <div className="stat-title">
                            Total totalRecovery
                        </div>

                        <div
                            style={{
                                fontSize: 34,
                                fontWeight: 700,
                                marginTop: 12,
                            }}
                        >
                            {Number(totals.totalRecovery || 0).toLocaleString("en-IN")}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        {/* ---------------- Summary Table ---------------- */}
        <div
            style={{
                marginTop: 24,
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #E2E8F0",
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                overflow: "hidden",
            }}
        >
            {/* Card Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 20px",
                    borderBottom: "1px solid #EDF2F7",
                }}
            >
                <span
                    style={{
                        width: 36,
                        height: 36,
                        background: "linear-gradient(135deg,#1B5E20,#43A047)",
                        borderRadius: 9,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: 16,
                    }}
                >
                    <i className="mdi mdi-cash-multiple" />
                </span>

                <div>
                    <div
                        style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#1A202C",
                        }}
                    >
                        Loan Monthly Review Summary { }
                    </div>

                    <div
                        style={{
                            fontSize: 11,
                            color: "#A0AEC0",
                        }}
                    >
                        {selectedRange
                            ? "VFC Wise Loan Revovery Review"
                            : selectedDivision
                                ? "Range Wise Loan Revovery Review"
                                : selectedCircle
                                    ? "Division Wise Loan Revovery Review"
                                    : "Circle Wise Loan Revovery Review"}
                    </div>
                </div>
                {/* Button at the end/right */}
                {/* <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        marginLeft: "auto",
                    }}
                    className="jfmc-switch"
                >
                    <button
                        onClick={{}}
                        className="btn btn-success"
                    >
                        Export Excel
                    </button>
                </div> */}
            </div>
            {/* Table */}
            <div style={{ overflowX: "auto" }}>
                <table
                    style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: "1200px",
                    }}
                >
                    <thead>
                        <tr
                            style={{
                                background: "#2E7D32",
                                color: "#fff",
                            }}
                        >
                            <th style={{ padding: 12, textAlign: "left" }}>
                                {selectedRange
                                    ? "VFC"
                                    : selectedDivision
                                        ? "Range"
                                        : selectedCircle
                                            ? "Division"
                                            : "Circle"}
                            </th>

                            {selectedRange ? (
                                <>
                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Year
                                    </th>

                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Total Loan Amount
                                    </th>

                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Total Recovery
                                    </th>
                                </>
                            ) : (
                                <>
                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Total VFCs
                                    </th>

                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Total Loan Amount
                                    </th>

                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Total Recovery
                                    </th>

                                    <th style={{ padding: 12 }}>
                                        {selectedCircle
                                            ? "Range Details"
                                            : selectedDivision
                                                ? "VFC Details"
                                                : "Division Details"}
                                    </th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {reviewData.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={selectedRange ? 3 : 6}
                                    style={{
                                        textAlign: "center",
                                        padding: 30,
                                        color: "#999",
                                    }}
                                >
                                    No Records Found
                                </td>
                            </tr>
                        ) : (
                            reviewData.map((item, index) => (
                                <tr
                                    key={item._id}
                                    style={{
                                        background:
                                            index % 2 === 0 ? "#fff" : "#F8FAFD",
                                    }}
                                >
                                    {/* Name */}
                                    <td
                                        style={{
                                            padding: "12px 15px",
                                            fontWeight: 600,
                                            color: "#1565C0",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <i
                                            className="mdi mdi-map-marker-outline"
                                            style={{ marginRight: 5 }}
                                        />
                                        <span>{item.name}</span>
                                        {item.lastUpdatedDate && (
                                            <span
                                                style={{
                                                    fontSize: "12px",
                                                    fontWeight: 700,
                                                    color: "#1565C0",
                                                }}
                                            >
                                                - Last Updated:{" "}
                                                {new Date(item.lastUpdatedDate).toLocaleDateString("en-GB")}
                                            </span>
                                        )}
                                    </td>

                                    {selectedRange ? (
                                        <>
                                            {/* Total Years */}
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                }}
                                            >
                                                {item.yearlyCollections?.length || 0}
                                            </td>
                                            {/* Loan Amount */}
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                ₹
                                                {(item.yearlyCollections || [])
                                                    .reduce(
                                                        (sum, year) => sum + (year.totalLoanAmount || 0),
                                                        0
                                                    )
                                                    .toLocaleString("en-IN")}
                                            </td>
                                            {/* Total Recovery */}
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                    fontWeight: 600,
                                                    color: "#166534",
                                                }}
                                            >
                                                ₹
                                                {(item.yearlyCollections || [])
                                                    .reduce(
                                                        (sum, year) => sum + (year.totalRecovery || 0),
                                                        0
                                                    )
                                                    .toLocaleString("en-IN")}
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            {/* Total VFC */}
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                }}
                                            >
                                                {badge(item.totalVfcs || 0, "total")}
                                            </td>

                                            {/* Total Loans */}
                                            <td style={{ textAlign: "center" }}>
                                                {badge(
                                                    `₹${Number(item.totalLoanAmount || 0).toLocaleString("en-IN")}`,
                                                    "held"
                                                )}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                {badge(
                                                    `₹${Number(item.totalRecovery || 0).toLocaleString("en-IN")}`,
                                                    "pending"
                                                )}
                                            </td>
                                            <td style={{ padding: 12, minWidth: 650 }}>

                                                {/* ======================= CIRCLE VIEW ======================= */}

                                                {!selectedCircle && !selectedDivision &&
                                                    Object.values(item.divisions || {}).map((division) => (
                                                        <div
                                                            key={division._id}
                                                            style={{
                                                                marginBottom: 15,
                                                                border: "1px solid #E5E7EB",
                                                                borderRadius: 10,
                                                                overflow: "hidden"
                                                            }}
                                                        >
                                                            {/* Division Header */}

                                                            <div
                                                                onClick={() => toggleDivision(division._id)}
                                                                style={{
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                    padding: "12px 15px",
                                                                    cursor: "pointer",
                                                                    background:
                                                                        division.totalLoans > 0
                                                                            ? "#ECFDF5"
                                                                            : "#FEF2F2"
                                                                }}
                                                            >
                                                                <div>

                                                                    <div
                                                                        style={{
                                                                            fontWeight: 700,
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: "6px",
                                                                            flexWrap: "wrap",
                                                                            color:
                                                                                division.totalLoans > 0
                                                                                    ? "#166534"
                                                                                    : "#991B1B"
                                                                        }}
                                                                    >
                                                                        <span>Division : {division.name}</span>

                                                                        {division.lastUpdatedDate && (
                                                                            <span
                                                                                style={{
                                                                                    fontSize: 12,
                                                                                    fontWeight: 500,
                                                                                    color: "#1565C0",
                                                                                }}
                                                                            >
                                                                                - Last Updated:{" "}
                                                                                {new Date(division.lastUpdatedDate).toLocaleDateString("en-GB")}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    
                                                                    <div
                                                                        style={{
                                                                            fontSize: 12,
                                                                            color: "#666",
                                                                            marginTop: 4
                                                                        }}
                                                                    >
                                                                        Collections : <b>{division.totalCollections}</b>

                                                                        {" | "}Amount :

                                                                        <b>
                                                                            ₹
                                                                            {Number(
                                                                                division.totalLoanAmount || 0
                                                                            ).toLocaleString("en-IN")}
                                                                        </b>
                                                                        {" | "}
                                                                        Recovery :
                                                                        <b>
                                                                            ₹{Number(division.totalRecovery || 0).toLocaleString("en-IN")}
                                                                        </b>

                                                                    </div>

                                                                </div>

                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: 15
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            padding: "4px 12px",
                                                                            borderRadius: 20,
                                                                            background:
                                                                                division.totalLoans > 0
                                                                                    ? "#DCFCE7"
                                                                                    : "#FEE2E2",
                                                                            color:
                                                                                division.totalLoans > 0
                                                                                    ? "#166534"
                                                                                    : "#991B1B",
                                                                            fontSize: 12,
                                                                            fontWeight: 600
                                                                        }}
                                                                    >
                                                                        {division.totalLoans} Loans
                                                                    </span>

                                                                    <i
                                                                        className={`mdi ${expandedDivisions[division._id]
                                                                            ? "mdi-chevron-up"
                                                                            : "mdi-chevron-down"
                                                                            }`}
                                                                        style={{ fontSize: 22 }}
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Expanded Division */}

                                                            {expandedDivisions[division._id] && (

                                                                <div
                                                                    style={{
                                                                        marginLeft: 20,
                                                                        marginTop: 10
                                                                    }}
                                                                >

                                                                    {Object.values(division.ranges || {}).map((range) => (

                                                                        <div
                                                                            key={range._id}
                                                                            style={{
                                                                                marginBottom: 15,
                                                                                borderLeft: "3px solid #22C55E",
                                                                                paddingLeft: 15
                                                                            }}
                                                                        >

                                                                            {/* Range Header */}

                                                                            <div
                                                                                onClick={() => toggleRange(range._id)}
                                                                                style={{
                                                                                    display: "flex",
                                                                                    justifyContent: "space-between",
                                                                                    alignItems: "center",
                                                                                    cursor: "pointer",
                                                                                    padding: "10px 0"
                                                                                }}
                                                                            >

                                                                                <div>

                                                                                    <div
                                                                                        style={{
                                                                                            display: "flex",
                                                                                            alignItems: "center",
                                                                                            gap: "6px",
                                                                                            flexWrap: "wrap",
                                                                                            fontWeight: 700,
                                                                                        }}
                                                                                    >
                                                                                        <span>Range : {range.name}</span>

                                                                                        {range.lastUpdatedDate && (
                                                                                            <span
                                                                                                style={{
                                                                                                    fontSize: 12,
                                                                                                    fontWeight: 700,
                                                                                                    color: "#1565C0",
                                                                                                }}
                                                                                            >
                                                                                                - Last Updated:{" "}
                                                                                                {new Date(range.lastUpdatedDate).toLocaleDateString("en-GB")}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                    <div
                                                                                        style={{
                                                                                            fontSize: 12,
                                                                                            color: "#666",
                                                                                            marginTop: 3
                                                                                        }}
                                                                                    >
                                                                                        Loans :

                                                                                        <b> {range.totalLoans}</b>

                                                                                        {" | "}Amount :

                                                                                        <b>
                                                                                            ₹
                                                                                            {Number(
                                                                                                range.totalLoanAmount || 0
                                                                                            ).toLocaleString("en-IN")}
                                                                                        </b>

                                                                                        {" | "}Recovery :

                                                                                        <b>{(range.totalRecovery).toLocaleString("en-IN")}</b>

                                                                                    </div>

                                                                                </div>

                                                                                <i
                                                                                    className={`mdi ${expandedRanges[range._id]
                                                                                        ? "mdi-chevron-up"
                                                                                        : "mdi-chevron-down"
                                                                                        }`}
                                                                                    style={{ fontSize: 22 }}
                                                                                />

                                                                            </div>

                                                                            {/* Expanded Range */}

                                                                            {expandedRanges[range._id] && (

                                                                                <div
                                                                                    style={{
                                                                                        marginLeft: 15,
                                                                                        marginTop: 10
                                                                                    }}
                                                                                >
                                                                                    {range.vfcs.map((vfc) => (
                                                                                        <div
                                                                                            key={vfc._id}
                                                                                            style={{
                                                                                                marginBottom: 15,
                                                                                                border: "1px solid #E5E7EB",
                                                                                                borderRadius: 8,
                                                                                                background: "#fff",
                                                                                                padding: 15
                                                                                            }}
                                                                                        >
                                                                                            {/* VFC Header */}

                                                                                            <div
                                                                                                style={{
                                                                                                    display: "flex",
                                                                                                    justifyContent: "space-between",
                                                                                                    alignItems: "center",
                                                                                                    marginBottom: 12
                                                                                                }}
                                                                                            >
                                                                                                <strong
                                                                                                    style={{
                                                                                                        color: "#1565C0",
                                                                                                        fontSize: 15
                                                                                                    }}
                                                                                                >
                                                                                                    {vfc.name}
                                                                                                </strong>

                                                                                                <span
                                                                                                    style={{
                                                                                                        padding: "4px 10px",
                                                                                                        borderRadius: 20,
                                                                                                        background:
                                                                                                            vfc.completed
                                                                                                                ? "#DCFCE7"
                                                                                                                : "#FEE2E2",
                                                                                                        color:
                                                                                                            vfc.completed
                                                                                                                ? "#166534"
                                                                                                                : "#991B1B",
                                                                                                        fontSize: 11,
                                                                                                        fontWeight: 600
                                                                                                    }}
                                                                                                >
                                                                                                    {vfc.completed
                                                                                                        ? "Recovery Available"
                                                                                                        : "No Recovery"}
                                                                                                </span>
                                                                                            </div>

                                                                                            {vfc.completed &&
                                                                                                vfc.yearlyCollections?.length > 0 && (
                                                                                                    <table
                                                                                                        style={{
                                                                                                            width: "100%",
                                                                                                            borderCollapse: "collapse",
                                                                                                            fontSize: 13
                                                                                                        }}
                                                                                                    >
                                                                                                        <thead>
                                                                                                            <tr
                                                                                                                style={{
                                                                                                                    background: "#F3F4F6"
                                                                                                                }}
                                                                                                            >
                                                                                                                <th style={{ padding: 8 }}>Year</th>
                                                                                                                <th style={{ padding: 8 }}>Loan Amount</th>
                                                                                                                <th style={{ padding: 8 }}>Recovery</th>
                                                                                                            </tr>
                                                                                                        </thead>

                                                                                                        <tbody>

                                                                                                            {vfc.yearlyCollections.map((yr) => (

                                                                                                                <tr key={yr.year}>
                                                                                                                    <td
                                                                                                                        style={{
                                                                                                                            padding: 8,
                                                                                                                            textAlign: "center"
                                                                                                                        }}
                                                                                                                    >
                                                                                                                        {yr.year}
                                                                                                                    </td>

                                                                                                                    <td
                                                                                                                        style={{
                                                                                                                            padding: 8,
                                                                                                                            textAlign: "right"
                                                                                                                        }}
                                                                                                                    >
                                                                                                                        ₹{Number(yr.totalLoanAmount).toLocaleString("en-IN")}
                                                                                                                    </td>

                                                                                                                    <td
                                                                                                                        style={{
                                                                                                                            padding: 8,
                                                                                                                            textAlign: "right"
                                                                                                                        }}
                                                                                                                    >
                                                                                                                        ₹{Number(yr.totalRecovery).toLocaleString("en-IN")}
                                                                                                                    </td>
                                                                                                                </tr>

                                                                                                            ))}

                                                                                                        </tbody>

                                                                                                    </table>
                                                                                                )}
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}

                                                                        </div>

                                                                    ))}

                                                                </div>

                                                            )}

                                                        </div>
                                                    ))}

                                                {/* ======================= DIVISION VIEW ======================= */}

                                                {selectedCircle && !selectedDivision &&
                                                    Object.values(item.ranges || {}).map((range) => (

                                                        <div
                                                            key={range._id}
                                                            style={{
                                                                marginBottom: 15,
                                                                border: "1px solid #E5E7EB",
                                                                borderRadius: 10,
                                                                overflow: "hidden"
                                                            }}
                                                        >

                                                            <div
                                                                onClick={() => toggleRange(range._id)}
                                                                style={{
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                    padding: "12px 15px",
                                                                    cursor: "pointer",
                                                                    background:
                                                                        range.totalCollections > 0
                                                                            ? "#ECFDF5"
                                                                            : "#FEF2F2"
                                                                }}
                                                            >
                                                                <div>
                                                                    <div
                                                                        style={{
                                                                            fontWeight: 700
                                                                        }}
                                                                    >
                                                                        Range : {range.name}
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            fontSize: 12,
                                                                            color: "#666",
                                                                            marginTop: 3
                                                                        }}
                                                                    >
                                                                        Collections : <b>{range.totalCollections}</b>
                                                                        {" | "}Loan Amount :
                                                                        <b>
                                                                            ₹{Number(
                                                                                range.totalLoanAmount || 0
                                                                            ).toLocaleString("en-IN")}
                                                                        </b>
                                                                        {" | "}Recovery :
                                                                        <b>
                                                                            ₹{Number(
                                                                                range.totalRecovery || 0
                                                                            ).toLocaleString("en-IN")}
                                                                        </b>
                                                                    </div>
                                                                </div>
                                                                <i
                                                                    className={`mdi ${expandedRanges[range._id]
                                                                        ? "mdi-chevron-up"
                                                                        : "mdi-chevron-down"
                                                                        }`}
                                                                    style={{ fontSize: 22 }}
                                                                />
                                                            </div>
                                                            {expandedRanges[range._id] && (
                                                                <div
                                                                    style={{
                                                                        padding: 15
                                                                    }}
                                                                >
                                                                    {range.vfcs.map((vfc) => (
                                                                        <div
                                                                            key={vfc._id}
                                                                            style={{
                                                                                border: "1px solid #E5E7EB",
                                                                                borderRadius: 8,
                                                                                padding: 15,
                                                                                marginBottom: 15
                                                                            }}
                                                                        >
                                                                            <strong
                                                                                style={{
                                                                                    color: "#1565C0"
                                                                                }}
                                                                            >
                                                                                {vfc.name}
                                                                            </strong>
                                                                            {vfc.yearlyCollections?.length > 0 ? (
                                                                                <table
                                                                                    style={{
                                                                                        width: "100%",
                                                                                        marginTop: 12,
                                                                                        borderCollapse: "collapse"
                                                                                    }}
                                                                                >
                                                                                    <thead>
                                                                                        <tr
                                                                                            style={{
                                                                                                background: "#F3F4F6"
                                                                                            }}
                                                                                        >
                                                                                            <th style={{ padding: 8 }}>
                                                                                                Year
                                                                                            </th>

                                                                                            <th style={{ padding: 8 }}>
                                                                                                Loan Amount
                                                                                            </th>
                                                                                            <th style={{ padding: 8 }}>
                                                                                                Recovery
                                                                                            </th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {vfc.yearlyCollections.map((yr) => (
                                                                                            <tr key={yr.year}>
                                                                                                <td
                                                                                                    style={{
                                                                                                        padding: 8,
                                                                                                        textAlign: "center"
                                                                                                    }}
                                                                                                >
                                                                                                    {yr.year}
                                                                                                </td>

                                                                                                <td
                                                                                                    style={{
                                                                                                        padding: 8,
                                                                                                        textAlign: "right"
                                                                                                    }}
                                                                                                >
                                                                                                    ₹{Number(
                                                                                                        yr.totalLoanAmount
                                                                                                    ).toLocaleString("en-IN")}
                                                                                                </td>
                                                                                                <td
                                                                                                    style={{
                                                                                                        padding: 8,
                                                                                                        textAlign: "right"
                                                                                                    }}
                                                                                                >
                                                                                                    ₹{Number(
                                                                                                        yr.totalRecovery
                                                                                                    ).toLocaleString("en-IN")}
                                                                                                </td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            ) : (
                                                                                <div
                                                                                    style={{
                                                                                        color: "#999",
                                                                                        marginTop: 10
                                                                                    }}
                                                                                >
                                                                                    No Recovery
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                {/* ======================= RANGE VIEW ======================= */}
                                                {selectedDivision &&
                                                    (item.vfcs || []).map((vfc) => (

                                                        <div
                                                            key={vfc._id}
                                                            style={{
                                                                border: "1px solid #E5E7EB",
                                                                borderRadius: 8,
                                                                padding: 15,
                                                                marginBottom: 15,
                                                                background: "#fff"
                                                            }}
                                                        >

                                                            <h6
                                                                style={{
                                                                    marginBottom: 12,
                                                                    color: "#1565C0"
                                                                }}
                                                            >
                                                                {vfc.name}
                                                            </h6>
                                                            {vfc.yearlyCollections?.length > 0 ? (
                                                                <table
                                                                    style={{
                                                                        width: "100%",
                                                                        borderCollapse: "collapse"
                                                                    }}
                                                                >
                                                                    <thead>
                                                                        <tr
                                                                            style={{
                                                                                background: "#F3F4F6"
                                                                            }}
                                                                        >
                                                                            <th style={{ padding: 8 }}>Year</th>
                                                                            <th style={{ padding: 8 }}>Loan Amount</th>
                                                                            <th style={{ padding: 8 }}>Recovery</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {vfc.yearlyCollections.map((yr) => (
                                                                            <tr key={yr.year}>
                                                                                <td
                                                                                    style={{
                                                                                        padding: 8,
                                                                                        textAlign: "center"
                                                                                    }}
                                                                                >
                                                                                    {yr.year}
                                                                                </td>
                                                                                <td
                                                                                    style={{
                                                                                        padding: 8,
                                                                                        textAlign: "right"
                                                                                    }}
                                                                                >
                                                                                    ₹{Number(
                                                                                        yr.totalLoanAmount
                                                                                    ).toLocaleString("en-IN")}
                                                                                </td>
                                                                                <td
                                                                                    style={{
                                                                                        padding: 8,
                                                                                        textAlign: "right"
                                                                                    }}
                                                                                >
                                                                                    ₹{Number(
                                                                                        yr.totalRecovery
                                                                                    ).toLocaleString("en-IN")}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            ) : (
                                                                <div
                                                                    style={{
                                                                        color: "#999"
                                                                    }}
                                                                >
                                                                    No Recovery
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    </div>;
};

export default MonthlyLoanRecoveryReview;
